/**
 * `callouts(subject, items, opts)`: labelled callouts for a subject, as plain scene objects.
 *
 * A callout is a label card, a thin leader and a dot where it lands. The cards sit outside the subject's front silhouette (its local
 * XY box, seen from +z), fanned to its left and right in the order of their anchors' heights, spaced so they do not overlap, with the
 * leaders un-crossed. Sizes follow the subject's scale. Everything is built from existing parts (a Layout card, Text, cube and sphere
 * primitives, Properties states, AnimationSequence clips) and hangs under one group inside the subject, in its local frame, so the
 * callouts follow the subject through moves and state changes.
 *
 * The pure helpers (placement, bounds) are exported for the tests. The host supplies the builder's own object calls.
 */
import { measureTextBox } from '../../behaviors/text/measure';
import type { TransformKeyframe } from '../../scenedata/behaviors/transformkeyframe';
import { hash } from './build-hash';
import { THEME_DEFAULTS } from './design-tokens';
import type { SpatialClipAsset, SpatialObjectDescriptor } from './spatial-types';

type V3 = [number, number, number];
export interface CalloutBox { min: V3; max: V3 }

export interface CalloutItem {
    /** A part (a handle or name of an object under the subject) or a point in the subject's local space (`[x, y]` lands on its front face). */
    at: string | [number, number] | V3;
    title?: string;
    text?: string;
}

export interface CalloutOptions {
    side?: 'auto' | 'left' | 'right' | 'both';
    /** Staggered entrance: the leader grows from its anchor, then the card rises and fades in. */
    enter?: boolean;
    /** The subject's local box, for a subject the build cannot measure (an asset, a clone). */
    bounds?: CalloutBox;
    name?: string;
}

/** What the callouts need of the builder. */
export interface CalloutHost {
    find(ref: string): SpatialObjectDescriptor | undefined;
    objects(): readonly SpatialObjectDescriptor[];
    create(template: 'empty' | 'cube' | 'sphere', name: string): string;
    freeName(base: string): string;
    parent(child: string, parent: string): void;
    move(id: string, x: number, y: number, z: number): void;
    rotate(id: string, x: number, y: number, z: number): void;
    scale(id: string, x: number, y: number, z: number): void;
    behavior(id: string, type: string, overrides: Record<string, unknown>): void;
    card(name: string, opts: Record<string, unknown>): string;
    label(content: string, opts: Record<string, unknown>): string;
    interaction(source: string): { onStart(): { setGroupState(target: string, state?: string): unknown } };
    warn(message: string): void;
    style?: Readonly<Record<string, unknown>>;
    /** Callout groups of earlier calls: left out of the subject's bounds. */
    ignore: Set<string>;
}

const PRIMITIVE_BOX: Record<string, CalloutBox> = {
    cube: { min: [-0.5, -0.5, -0.5], max: [0.5, 0.5, 0.5] },
    sphere: { min: [-0.5, -0.5, -0.5], max: [0.5, 0.5, 0.5] },
    cylinder: { min: [-0.5, -0.5, -0.5], max: [0.5, 0.5, 0.5] },
    plane: { min: [-0.5, -0.5, 0], max: [0.5, 0.5, 0] },
};

const DEG = Math.PI / 180;

/** A rotation as a quaternion [x, y, z, w]. The pure build collector imports nothing from the engine's scene code or Babylon (a test pins it), so the few rotation operations it needs are here. */
type Quat = [number, number, number, number];

/** Euler degrees as the engine reads them (`Quaternion.FromEulerAngles`: yaw about y, pitch about x, roll about z). */
function eulerToQuat(rx: number, ry: number, rz: number): Quat {
    const [sp, cp] = [Math.sin(rx * DEG / 2), Math.cos(rx * DEG / 2)], [sy, cy] = [Math.sin(ry * DEG / 2), Math.cos(ry * DEG / 2)], [sr, cr] = [Math.sin(rz * DEG / 2), Math.cos(rz * DEG / 2)];
    return [cy * sp * cr + sy * cp * sr, sy * cp * cr - cy * sp * sr, cy * cp * sr - sy * sp * cr, cy * cp * cr + sy * sp * sr];
}

/** The Euler degrees `eulerToQuat` turns back into `q` (the engine's `toEulerAngles`). */
export function quatToEuler([x, y, z, w]: Quat): V3 {
    const zAxisY = y * z - x * w, limit = 0.4999999;
    if (zAxisY < -limit) return [90, 2 * Math.atan2(y, w) / DEG, 0];
    if (zAxisY > limit) return [-90, 2 * Math.atan2(y, w) / DEG, 0];
    const sqw = w * w, sqz = z * z, sqx = x * x, sqy = y * y;
    return [Math.asin(-2 * zAxisY) / DEG, Math.atan2(2 * (z * x + y * w), sqz - sqx - sqy + sqw) / DEG, Math.atan2(2 * (x * y + z * w), -sqz - sqx + sqy + sqw) / DEG];
}

function rotate(q: Quat, v: V3): V3 {
    const [x, y, z, w] = q;
    // v + 2w (q x v) + 2 q x (q x v)
    const cx = y * v[2] - z * v[1], cy = z * v[0] - x * v[2], cz = x * v[1] - y * v[0];
    return [v[0] + 2 * (w * cx + y * cz - z * cy), v[1] + 2 * (w * cy + z * cx - x * cz), v[2] + 2 * (w * cz + x * cy - y * cx)];
}

/** A point carried through a descriptor's own scale, rotation and position, in that order. */
type Place = (p: V3) => V3;
function placeOf(d: SpatialObjectDescriptor): Place {
    const q = eulerToQuat(...(d.rotation ?? [0, 0, 0])), [sx, sy, sz] = d.scale ?? [1, 1, 1], [px, py, pz] = d.position ?? [0, 0, 0];
    return p => { const r = rotate(q, [p[0] * sx, p[1] * sy, p[2] * sz]); return [r[0] + px, r[1] + py, r[2] + pz]; };
}

function corners(box: CalloutBox, place: Place): V3[] {
    const out: V3[] = [];
    for (const x of [box.min[0], box.max[0]]) for (const y of [box.min[1], box.max[1]]) for (const z of [box.min[2], box.max[2]]) out.push(place([x, y, z]));
    return out;
}

function boxOf(points: V3[]): CalloutBox | null {
    if (!points.length) return null;
    const min: V3 = [Infinity, Infinity, Infinity], max: V3 = [-Infinity, -Infinity, -Infinity];
    for (const p of points) for (let i = 0; i < 3; i++) { min[i] = Math.min(min[i], p[i]); max[i] = Math.max(max[i], p[i]); }
    return { min, max };
}

/** The box of what renders under `desc`, in desc's own frame (its transform not applied); null when nothing the build can measure. */
export function localBox(host: Pick<CalloutHost, 'objects' | 'ignore'>, desc: SpatialObjectDescriptor, unmeasured: string[] = [], depth = 0): CalloutBox | null {
    if (depth > 64) return null;
    const points: V3[] = [];
    const own = PRIMITIVE_BOX[desc.template];
    if (!own && (desc.template === 'asset' || desc.template === 'clone') && desc.active !== false) unmeasured.push(desc.name);
    if (own) points.push(...corners(own, p => p));
    for (const kid of host.objects()) {
        if (kid.parentId !== desc.id || host.ignore.has(kid.id) || kid.active === false) continue;
        const box = localBox(host, kid, unmeasured, depth + 1);
        if (box) points.push(...corners(box, placeOf(kid)));
    }
    return boxOf(points);
}

/** `desc`'s box in `ancestor`'s frame (ancestor's own transform not applied); null when desc is not under it or has no extent. */
function boxIn(host: Pick<CalloutHost, 'objects' | 'ignore' | 'find'>, desc: SpatialObjectDescriptor, ancestor: SpatialObjectDescriptor): CalloutBox | null {
    const box = localBox(host, desc);
    if (!box) return null;
    const chain: Place[] = [];
    for (let at: SpatialObjectDescriptor | undefined = desc; at !== ancestor;) {
        if (!at) return null;
        chain.push(placeOf(at));
        at = at.parentId ? host.find(at.parentId) : undefined;
    }
    return boxOf(corners(box, p => chain.reduce((q, place) => place(q), p)));
}

// ---- placement ------------------------------------------------------------------------------------------------------------------

/**
 * Card centres for cards listed top to bottom, each wanting `want[k]`, none closer than `gap` between faces: the least-squares fit
 * to the wishes under the spacing (pool adjacent violators), so a crowd spreads about its middle instead of piling downwards.
 */
export function spread(want: number[], heights: number[], gap: number): number[] {
    const n = want.length;
    const offset: number[] = [];
    for (let k = 0; k < n; k++) offset.push(k === 0 ? 0 : offset[k - 1] + (heights[k - 1] + heights[k]) / 2 + gap);
    // u_k = p_k + offset_k must not increase down the list.
    const blocks: { sum: number; count: number }[] = [];
    for (let k = 0; k < n; k++) {
        blocks.push({ sum: want[k] + offset[k], count: 1 });
        while (blocks.length > 1) {
            const b = blocks[blocks.length - 1], a = blocks[blocks.length - 2];
            if (a.sum / a.count >= b.sum / b.count) break;
            blocks.splice(-2, 2, { sum: a.sum + b.sum, count: a.count + b.count });
        }
    }
    const out: number[] = [];
    for (const b of blocks) for (let i = 0; i < b.count; i++) out.push(b.sum / b.count - offset[out.length]);
    return out;
}

type P2 = [number, number];
const cross = (o: P2, a: P2, b: P2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
/** Whether two segments cross (touching or collinear segments do not count). */
export function segmentsCross(a: P2, b: P2, c: P2, d: P2): boolean {
    const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export interface Planned { index: number; anchor: V3; w: number; h: number; side: 'left' | 'right'; y: number; edgeX: number; centerX: number }

/**
 * Stack the cards of one side in slots one card tall apart, the slots fitted to the anchors' heights, then swap any two cards whose
 * leaders cross: each swap shortens the leaders' total length, so it ends with none crossing.
 */
export function planSide(cards: Planned[], column: number, sign: -1 | 1, gap: number): void {
    if (!cards.length) return;
    cards.sort((a, b) => b.anchor[1] - a.anchor[1] || a.anchor[0] - b.anchor[0]);
    const pitch = Math.max(...cards.map(c => c.h));
    const slots = spread(cards.map(c => c.anchor[1]), cards.map(() => pitch), gap);
    cards.forEach((c, k) => { c.y = slots[k]; c.edgeX = column; c.centerX = column + sign * c.w / 2; });
    const crosses = (a: Planned, b: Planned) => segmentsCross([a.edgeX, a.y], [a.anchor[0], a.anchor[1]], [b.edgeX, b.y], [b.anchor[0], b.anchor[1]]);
    for (let pass = 0; pass < 500; pass++) {
        let swapped = false;
        for (let a = 0; a < cards.length; a++) for (let b = a + 1; b < cards.length; b++) {
            if (!crosses(cards[a], cards[b])) continue;
            [cards[a].y, cards[b].y] = [cards[b].y, cards[a].y];
            swapped = true;
        }
        if (!swapped) return;
    }
}

// ---- building ---------------------------------------------------------------------------------------------------------------------

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;
const easeOut = (t: number) => 1 - (1 - t) ** 3;

/** Keys that hold `from` until `delay`, then move to `to` over `duration` along an ease-out. */
function eased(delay: number, duration: number, from: V3, to: V3, channel: 'position' | 'scale'): TransformKeyframe[] {
    const keys: TransformKeyframe[] = [{ time: 0, [channel]: from }];
    if (delay > 0) keys.push({ time: round6(delay), [channel]: from });
    const steps = 8;
    for (let i = 1; i <= steps; i++) {
        const e = easeOut(i / steps);
        keys.push({ time: round6(delay + duration * i / steps), [channel]: from.map((v, j) => round6(v + (to[j] - v) * e)) as V3 });
    }
    return keys;
}

export const CALLOUT_STAGGER = 0.18;
const LEADER_TIME = 0.35, CARD_TIME = 0.4;

export function buildCallouts(host: CalloutHost, subject: string, items: readonly CalloutItem[], opts: CalloutOptions = {}): string {
    if (typeof subject !== 'string') throw new Error('callouts(subject, items, opts): subject must be the handle of the group the callouts belong to.');
    if (!items?.length || items.some(item => !item || typeof item !== 'object')) throw new Error('callouts(subject, items): items must be a list of { at, title?, text? }.');
    const side = opts.side ?? 'auto';
    if (!['auto', 'left', 'right', 'both'].includes(side)) throw new Error(`callouts: side must be auto, left, right or both, got ${JSON.stringify(side)}.`);
    const subjectDesc = host.find(subject);
    if (opts.bounds && ![...opts.bounds.min, ...opts.bounds.max].every(v => typeof v === 'number' && Number.isFinite(v)))
        throw new Error('callouts: bounds must be { min: [x, y, z], max: [x, y, z] } of finite numbers.');
    const unmeasured: string[] = [];
    const box = opts.bounds ?? (subjectDesc ? localBox(host, subjectDesc, unmeasured) : null);
    if (unmeasured.length && !opts.bounds)
        host.warn(`callouts(): ${unmeasured.slice(0, 4).map(n => `"${n}"`).join(', ')}${unmeasured.length > 4 ? ' and more' : ''} cannot be measured (an asset or clone), so the cards are placed around the rest of "${subject}"; pass { bounds: { min, max } } to place them around all of it.`);
    if (!box) throw new Error(`callouts(): cannot measure "${subject}" (a group of cube, sphere, cylinder and plane parts is measured; an asset or clone is not). Pass { bounds: { min: [x, y, z], max: [x, y, z] } } in its local space.`);
    const width = box.max[0] - box.min[0], height = box.max[1] - box.min[1];
    const S = Math.max(width, height, 1e-3);
    const cx = (box.min[0] + box.max[0]) / 2;

    // Sizes: everything is a share of the subject's largest side, so a 30 cm speaker and a 3 m rack get the same look.
    const emBody = 0.052 * S, emTitle = 0.065 * S, pad = 0.04 * S, lineGap = 0.012 * S, cardGap = 0.035 * S, wrapAt = 0.5 * S;
    const thick = Math.max(0.0008, 0.007 * S), dotSize = 0.03 * S;
    const style = host.style ?? {};
    const family = (k: string): string => { const v = style[k]; return typeof v === 'string' && v ? v : 'Roboto'; };
    const tokens = Object.entries(THEME_DEFAULTS).map(([name, fallback]) => {
        const given = style[name];
        return { type: name.endsWith('Font') ? 'string' : 'color', name, value: typeof given === 'string' && given ? given : fallback };
    });

    // Anchors.
    const anchors: V3[] = items.map((item, i) => {
        const at = item.at;
        if (Array.isArray(at)) {
            if ((at.length !== 2 && at.length !== 3) || at.some(v => typeof v !== 'number' || !Number.isFinite(v)))
                throw new Error(`callouts: item ${i} "at" must be a part or a point [x, y] / [x, y, z], got ${JSON.stringify(at)}.`);
            return [at[0], at[1], at[2] ?? box.max[2]];
        }
        const part = typeof at === 'string' ? host.find(at) : undefined;
        if (!part || !subjectDesc) throw new Error(`callouts: item ${i} "at" ${JSON.stringify(at)} is not an object built in this call under "${subject}"; use a handle from this build or a point [x, y, z] in the subject's local space.`);
        const partBox = part === subjectDesc ? box : boxIn(host, part, subjectDesc);
        if (!partBox) throw new Error(`callouts: item ${i} part "${part.name}" is not under "${subject}", or has no extent to point at; give a point instead.`);
        return [(partBox.min[0] + partBox.max[0]) / 2, (partBox.min[1] + partBox.max[1]) / 2, partBox.max[2]];
    });

    // Card sizes, measured with the engine's own text layout so the fixed card holds its words.
    const measured = items.map(item => {
        const title = item.title ? measureTextBox({ text: item.title, fontSize: emTitle * 100, fontFamily: family('headerFont'), fontWeight: 'bold', wrapWidth: wrapAt, anchorX: 'left', anchorY: 'top' }) : null;
        const text = item.text ? measureTextBox({ text: item.text, fontSize: emBody * 100, fontFamily: family('bodyFont'), wrapWidth: wrapAt, anchorX: 'left', anchorY: 'top' }) : null;
        if (!title && !text) throw new Error('callouts: every item needs a title or text.');
        // A margin on the width: the rendered face may differ a little from the measured one.
        const inner = Math.min(wrapAt, Math.max(title?.width ?? 0, text?.width ?? 0) * 1.06 + 0.2 * emBody);
        return { w: inner + 2 * pad, h: (title?.height ?? 0) + (text?.height ?? 0) + (title && text ? lineGap : 0) + 2 * pad, inner };
    });

    // Sides.
    const half = Math.ceil(items.length / 2);
    const sides: ('left' | 'right')[] = items.map((_, i) => side === 'left' || side === 'right' ? side : anchors[i][0] < cx ? 'left' : 'right');
    if (side === 'both') {
        const byX = items.map((_, i) => i).sort((a, b) => anchors[a][0] - anchors[b][0]);
        byX.forEach((i, k) => { sides[i] = k < half ? 'left' : 'right'; });
    } else if (side === 'auto') {
        // Nearest side, but not more than half the cards on one: the extra ones nearest the middle go over to the other.
        for (const s of ['left', 'right'] as const) {
            const mine = items.map((_, i) => i).filter(i => sides[i] === s).sort((a, b) => Math.abs(anchors[b][0] - cx) - Math.abs(anchors[a][0] - cx));
            while (mine.length > half) sides[mine.pop()!] = s === 'left' ? 'right' : 'left';
        }
    }

    const gapOut = 0.1 * S, zFront = box.max[2] + 0.03 * S;
    const planned: Planned[] = items.map((_, index) => ({ index, anchor: anchors[index], w: measured[index].w, h: measured[index].h, side: sides[index], y: 0, edgeX: 0, centerX: 0 }));
    planSide(planned.filter(p => p.side === 'left'), box.min[0] - gapOut, -1, cardGap);
    planSide(planned.filter(p => p.side === 'right'), box.max[0] + gapOut, 1, cardGap);

    const leg = (p: Planned): [[number, number], [number, number]] => [[p.edgeX, p.y], [p.anchor[0], p.anchor[1]]];
    const crossed = planned.flatMap((p, i) => planned.slice(i + 1).filter(q => segmentsCross(...leg(p), ...leg(q))).map(q => `${p.index + 1} and ${q.index + 1}`));
    if (crossed.length) host.warn(`callouts(): the leaders of callouts ${crossed.slice(0, 3).join(', ')} cross; give the anchors more room, or use side "both".`);

    // Objects.
    const base = opts.name ?? 'Callouts';
    // Every object of this call is named from a prefix no earlier call used, so name paths stay unambiguous.
    // freeName returns the path-safe name ("/" becomes "-"), so the stem must already be path-safe or the two never match.
    const stem = (opts.name ?? 'Callout').replaceAll('/', '-');
    let prefix = stem;
    for (let k = 2; host.freeName(`${prefix} 1 Card`) !== `${prefix} 1 Card`; k++) prefix = `${stem} (${k})`;
    const root = host.create('empty', host.freeName(base));
    host.parent(root, subject);
    host.ignore.add(root);
    host.behavior(root, 'Properties', { properties: tokens, ...(opts.enter ? { states: { away: { values: {} }, home: { values: {} } } } : {}) });
    const clips: { name: string; keyframes: TransformKeyframe[] }[] = [];
    const clipSrc = () => `/generated/callouts-${hash(JSON.stringify(clips))}.glb`;
    const sequence: { id: string; clip: string }[] = [];

    planned.sort((a, b) => a.index - b.index).forEach((p, order) => {
        const item = items[p.index];
        const label = `${prefix} ${p.index + 1}`;
        const delay = round6(order * CALLOUT_STAGGER);

        // The card: a fixed-size Layout card, so its place is known to the metre.
        const card = host.card(`${label} Card`, {
            direction: 'y', gap: lineGap, padding: pad, width: p.w, height: p.h, align: 'Start', justify: 'Start',
            color: '{cardColor}', cornerRadius: 0.35 * pad,
        });
        host.parent(card, root);
        host.move(card, round6(p.centerX), round6(p.y), round6(zFront));
        if (item.title) {
            const t = host.label(item.title, { fontSize: emTitle * 100, color: '{cardTextColor}', bold: true, name: `${label} Title`, maxWidth: measured[p.index].inner });
            host.parent(t, card);
            host.behavior(t, 'Text', { fontFamily: '{headerFont}', anchorX: 'left' });
        }
        if (item.text) {
            const t = host.label(item.text, { fontSize: emBody * 100, color: '{cardTextColor}', name: `${label} Text`, maxWidth: measured[p.index].inner });
            host.parent(t, card);
            host.behavior(t, 'Text', { fontFamily: '{bodyFont}', anchorX: 'left' });
        }

        // The leader: a group at the anchor turned toward the card's edge, holding a rod from the anchor out to the edge.
        const from = p.anchor, to: V3 = [p.edgeX, p.y, zFront];
        const delta: V3 = [to[0] - from[0], to[1] - from[1], to[2] - from[2]];
        const length = Math.max(Math.hypot(...delta), 1e-4);
        const e = quatToEuler(arc([delta[0] / length, delta[1] / length, delta[2] / length]));
        const leader = host.create('empty', `${label} Leader`);
        host.parent(leader, root);
        host.move(leader, round6(from[0]), round6(from[1]), round6(from[2]));
        host.rotate(leader, round6(e[0]), round6(e[1]), round6(e[2]));
        const rod = host.create('cube', `${label} Rod`);
        host.parent(rod, leader);
        host.move(rod, round6(length / 2), 0, 0);
        host.scale(rod, round6(length), round6(thick), round6(thick));
        accent(host, rod);

        const dot = host.create('sphere', `${label} Dot`);
        host.parent(dot, root);
        host.move(dot, round6(from[0]), round6(from[1]), round6(from[2]));
        host.scale(dot, round6(dotSize), round6(dotSize), round6(dotSize));
        accent(host, dot);

        if (opts.enter) {
            // The dot lands, the leader grows out of it, then the card rises and fades in.
            const one: V3 = [1, 1, 1], dotRest: V3 = [round6(dotSize), round6(dotSize), round6(dotSize)];
            const dotKeys = eased(delay, 0.2, [0.0001, 0.0001, 0.0001], dotRest, 'scale');
            const leaderKeys = eased(delay + 0.1, LEADER_TIME, [0.001, 1, 1], one, 'scale');
            const rise = 2 * emBody;
            const cardRest: V3 = [round6(p.centerX), round6(p.y), round6(zFront)];
            const cardKeys = eased(delay + 0.1 + LEADER_TIME * 0.6, CARD_TIME, [cardRest[0], round6(cardRest[1] - rise), cardRest[2]], cardRest, 'position');
            for (const [id, keys, name] of [[dot, dotKeys, `${label} dot`], [leader, leaderKeys, `${label} leader`], [card, cardKeys, `${label} card`]] as const) {
                clips.push({ name, keyframes: keys });
                sequence.push({ id, clip: name });
            }
            host.behavior(card, 'Properties', {
                properties: [{ name: 'calloutIn', type: 'number', value: 1 }],
                initialState: 'away',
                states: {
                    away: { values: { calloutIn: 0 }, transition: { duration: 0 } },
                    home: { values: { calloutIn: 1 }, transition: { duration: CARD_TIME, delay: round6(delay + 0.1 + LEADER_TIME * 0.6), easing: 'easeOut' } },
                },
            });
            fade(host, card);
        }
    });

    if (opts.enter) {
        const src = clipSrc();
        const rootDesc = host.find(root)!;
        const asset: SpatialClipAsset = { src, clips, fps: 30 };
        rootDesc.clips = [...(rootDesc.clips ?? []), asset];
        for (const { id, clip } of sequence) host.behavior(id, 'AnimationSequence', { states: { enter: { src, clip } } });
        host.interaction(root).onStart().setGroupState(root, 'home');
    }
    return root;
}

/** The rotation that turns +X onto the unit vector `d`. */
function arc(d: V3): Quat {
    if (d[0] > 0.999999) return [0, 0, 0, 1];
    if (d[0] < -0.999999) return [0, 0, 1, 0];
    // axis = X x d = (0, -d.z, d.y), w = 1 + X.d
    const q: Quat = [0, -d[2], d[1], 1 + d[0]], n = Math.hypot(...q);
    return [q[0] / n, q[1] / n, q[2] / n, q[3] / n];
}

function accent(host: CalloutHost, id: string): void {
    host.behavior(id, 'Renderer', { materials: [{ targetMaterialIndex: 0, baseColorFactor: '{accentColor}', emissiveFactor: '{accentColor}' }] });
}

/** Bind a card's text and body to its `calloutIn` property so the entrance can fade it. */
function fade(host: CalloutHost, card: string): void {
    const walk = (id: string, seen = new Set<string>()): void => {
        if (seen.has(id)) return;
        seen.add(id);
        const d = host.find(id)!;
        for (const b of d.behaviors ?? []) {
            if (b.type === 'Text') host.behavior(id, 'Text', { ...(b.overrides ?? {}), opacity: '{calloutIn}' });
            else if (b.type === 'Renderer') host.behavior(id, 'Renderer', { ...(b.overrides ?? {}), opacity: '{calloutIn}' });
        }
        for (const kid of host.objects()) if (kid.parentId === id) walk(kid.id, seen);
    };
    walk(card);
}
